import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraBadge, SpecraButton, SpecraCard, SpecraCardContent, SpecraCardHeader, SpecraCardTitle, SpecraEmptyState, SpecraFormField, SpecraOverlayDialog, SpecraSelect, SpecraText, SpecraTextarea } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { HistoryLink } from '../Library/ReleaseHistory';
import { libraryReleaseClient } from '../Library/reuseClient';
import { TRANSFER_MODES } from '../Library/reuseHooks';
export const THEME_RECEIVERS = {
    solution: 'Solution',
    competition: 'Competition analysis',
    win_themes: 'Win Themes',
    outputs: 'Capture outputs',
    proposal: 'Proposal'
};
const ids = Testids.customerThemeEvidence;
const queryKey = [
    'customer-theme-evidence'
];
export const themeReviewLabel = (status)=>({
        resolved: 'Up to date',
        current: 'Current and reviewed',
        source_changed: 'Source changed — review needed',
        source_unavailable: 'Source unavailable — review needed',
        superseded: 'Superseded — review needed',
        withdrawn: 'Withdrawn — review needed',
        source_withdrawn: 'Withdrawn — review needed'
    })[status] || 'Review needed';
/** Receiver content is resolved by Library, including ownership and audience checks. */ export function CustomerThemeEvidence({ receiver, objectId, readOnly = false }) {
    const cache = useQueryClient();
    const generation = useRef(0);
    const [releaseId, setReleaseId] = useState('');
    const [mode, setMode] = useState('live_reference');
    const [derivative, setDerivative] = useState();
    const [openError, setOpenError] = useState(false);
    const [opening, setOpening] = useState(false);
    const [addOpen, setAddOpen] = useState(false);
    const [editing, setEditing] = useState('');
    const [wording, setWording] = useState('');
    const receivingObjectType = `customer_theme.${receiver}`;
    const available = useQuery({
        queryKey: [
            ...queryKey,
            'available'
        ],
        gcTime: 0,
        retry: false,
        queryFn: async ()=>(await libraryReleaseClient.listCustomerContexts({})).summaries.filter((row)=>row.contextKind === 'theme' && row.status === 'current')
    });
    const reuses = useQuery({
        queryKey: [
            ...queryKey,
            receivingObjectType,
            objectId
        ],
        gcTime: 0,
        retry: false,
        queryFn: async ()=>(await libraryReleaseClient.listLibraryReuses({
                receivingObjectType,
                receivingObjectId: objectId
            })).reuses
    });
    useEffect(()=>{
        if (reuses.isFetching || reuses.isError) {
            generation.current++;
            setDerivative(undefined);
            setOpening(false);
        }
    }, [
        reuses.isFetching,
        reuses.isError
    ]);
    useEffect(()=>()=>{
            generation.current++;
        }, [
        receiver,
        objectId
    ]);
    const save = useMutation({
        mutationFn: async ()=>{
            if (editing) await libraryReleaseClient.editAdoptedLibraryReuse({
                reuseId: editing,
                content: wording
            });
            else await libraryReleaseClient.createLibraryReuse({
                receivingObjectType,
                receivingObjectId: objectId,
                releaseId,
                transferMode: mode
            });
        },
        onSuccess: async ()=>{
            setEditing('');
            setReleaseId('');
            setAddOpen(false);
            await cache.invalidateQueries({
                queryKey
            });
        }
    });
    async function open(row) {
        const current = ++generation.current;
        setDerivative(undefined);
        setOpenError(false);
        setOpening(true);
        try {
            const result = await libraryReleaseClient.getCustomerContext({
                libraryItemId: row.libraryItemId,
                releaseId: row.releaseId
            });
            if (current === generation.current) setDerivative(result.summary);
        } catch  {
            if (current === generation.current) setOpenError(true);
        } finally{
            if (current === generation.current) setOpening(false);
        }
    }
    return /*#__PURE__*/ _jsxs(SpecraCard, {
        "data-testid": ids.panel,
        id: `theme-evidence-${receiver}`,
        children: [
            /*#__PURE__*/ _jsxs(SpecraCardHeader, {
                className: "flex flex-row items-center justify-between space-y-0",
                children: [
                    /*#__PURE__*/ _jsxs(SpecraCardTitle, {
                        children: [
                            THEME_RECEIVERS[receiver],
                            " · Customer Theme evidence"
                        ]
                    }),
                    !readOnly && !!reuses.data?.length && /*#__PURE__*/ _jsx(SpecraButton, {
                        size: "sm",
                        onClick: ()=>setAddOpen(true),
                        "data-testid": ids.addButton,
                        children: "Add theme evidence"
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs(SpecraCardContent, {
                className: "space-y-3",
                children: [
                    /*#__PURE__*/ _jsx(SpecraText, {
                        as: "p",
                        size: "meta",
                        children: "Customer Themes are evidence. Solution decisions, competitor weaknesses and Win Theme conclusions retain their own rationale. Edited wording belongs to this pursuit; source changes never overwrite it."
                    }),
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        variant: "secondary",
                        onClick: ()=>{
                            setDerivative(undefined);
                            void cache.invalidateQueries({
                                queryKey
                            });
                        },
                        children: "Refresh theme evidence"
                    }),
                    (reuses.isFetching || available.isFetching) && /*#__PURE__*/ _jsx(SpecraText, {
                        as: "p",
                        "aria-live": "polite",
                        children: "Loading theme evidence…"
                    }),
                    (reuses.isError || available.isError) && /*#__PURE__*/ _jsx(SpecraText, {
                        as: "p",
                        role: "alert",
                        children: "Theme evidence could not be loaded. Refresh to retry."
                    }),
                    !reuses.isFetching && !reuses.isError && !reuses.data?.length && /*#__PURE__*/ _jsx(SpecraEmptyState, {
                        size: "compact",
                        title: "No Customer Theme evidence linked here.",
                        description: "Use a customer theme as evidence.",
                        "data-testid": ids.emptyState,
                        action: readOnly ? undefined : /*#__PURE__*/ _jsx(SpecraButton, {
                            size: "sm",
                            onClick: ()=>setAddOpen(true),
                            "data-testid": ids.addButton,
                            children: "Add theme evidence"
                        })
                    }),
                    !readOnly && /*#__PURE__*/ _jsx(SpecraOverlayDialog, {
                        open: addOpen,
                        onOpenChange: setAddOpen,
                        title: "Add theme evidence",
                        size: "md",
                        testId: ids.dialog,
                        footerProps: {
                            onCancel: ()=>setAddOpen(false),
                            confirmLabel: 'Use theme',
                            onConfirm: ()=>save.mutate(),
                            confirmProps: {
                                disabled: !releaseId || save.isPending || !!editing,
                                'data-testid': ids.link
                            }
                        },
                        children: /*#__PURE__*/ _jsxs("div", {
                            className: "space-y-3",
                            children: [
                                /*#__PURE__*/ _jsx(SpecraFormField, {
                                    label: "Customer theme",
                                    children: /*#__PURE__*/ _jsxs(SpecraSelect, {
                                        "aria-label": "Customer theme",
                                        "data-testid": ids.release,
                                        value: releaseId,
                                        onChange: (e)=>setReleaseId(e.target.value),
                                        children: [
                                            /*#__PURE__*/ _jsx("option", {
                                                value: "",
                                                children: "Select a customer theme"
                                            }),
                                            available.data?.map((row)=>/*#__PURE__*/ _jsxs("option", {
                                                    value: row.releaseId,
                                                    children: [
                                                        row.title,
                                                        " · v",
                                                        row.version
                                                    ]
                                                }, row.releaseId))
                                        ]
                                    })
                                }),
                                !available.isFetching && !available.isError && !available.data?.length && /*#__PURE__*/ _jsx(SpecraText, {
                                    as: "p",
                                    children: "No customer themes are available to you yet."
                                }),
                                /*#__PURE__*/ _jsxs("details", {
                                    "data-testid": ids.advanced,
                                    children: [
                                        /*#__PURE__*/ _jsx("summary", {
                                            className: "cursor-pointer text-sm font-medium",
                                            children: "Advanced"
                                        }),
                                        /*#__PURE__*/ _jsx("div", {
                                            className: "pt-2",
                                            children: /*#__PURE__*/ _jsx(SpecraFormField, {
                                                label: "Use as",
                                                children: /*#__PURE__*/ _jsx(SpecraSelect, {
                                                    "aria-label": "Use as",
                                                    "data-testid": ids.mode,
                                                    value: mode,
                                                    onChange: (e)=>setMode(e.target.value),
                                                    children: TRANSFER_MODES.map((m)=>/*#__PURE__*/ _jsx("option", {
                                                            value: m.value,
                                                            children: m.label
                                                        }, m.value))
                                                })
                                            })
                                        })
                                    ]
                                })
                            ]
                        })
                    }),
                    save.isError && /*#__PURE__*/ _jsx(SpecraText, {
                        as: "p",
                        role: "alert",
                        children: "Could not save theme evidence. Confirm destination edit permission and access to the theme, then retry. Your wording is retained."
                    }),
                    !reuses.isFetching && !reuses.isError && reuses.data?.map((row)=>/*#__PURE__*/ _jsxs("div", {
                            "data-testid": ids.row,
                            className: "space-y-2 rounded-md border p-3",
                            children: [
                                /*#__PURE__*/ _jsxs(SpecraText, {
                                    as: "p",
                                    children: [
                                        row.title,
                                        " · ",
                                        TRANSFER_MODES.find((m)=>m.value === row.transferMode)?.label,
                                        " · basis v",
                                        row.sourceVersion,
                                        row.resolvedVersion > 0 && ` · displayed v${row.resolvedVersion}`
                                    ]
                                }),
                                /*#__PURE__*/ _jsx(SpecraBadge, {
                                    variant: row.status === 'resolved' ? 'success' : 'warning',
                                    "data-testid": ids.impact,
                                    children: themeReviewLabel(row.status)
                                }),
                                /*#__PURE__*/ _jsx(SpecraText, {
                                    as: "p",
                                    className: "whitespace-pre-wrap",
                                    "data-testid": ids.content,
                                    children: row.content
                                }),
                                /*#__PURE__*/ _jsxs(SpecraText, {
                                    as: "p",
                                    size: "meta",
                                    children: [
                                        "Based on version ",
                                        row.sourceVersion,
                                        ".",
                                        ' ',
                                        row.transferMode === 'frozen_snapshot' ? 'This copy stays as it was.' : row.transferMode === 'adopted' ? 'Your copy stays as it is until you edit it.' : 'Stays in sync with the source.'
                                    ]
                                }),
                                /*#__PURE__*/ _jsx(SpecraButton, {
                                    variant: "secondary",
                                    "data-testid": ids.derivative,
                                    disabled: opening,
                                    onClick: ()=>void open(row),
                                    children: "Open the customer insight"
                                }),
                                !readOnly && row.editable && row.transferMode === 'adopted' && /*#__PURE__*/ _jsx(SpecraButton, {
                                    variant: "secondary",
                                    onClick: ()=>{
                                        setEditing(row.reuseId);
                                        setWording(row.content);
                                    },
                                    children: "Edit wording"
                                }),
                                editing === row.reuseId && /*#__PURE__*/ _jsxs(_Fragment, {
                                    children: [
                                        /*#__PURE__*/ _jsx(SpecraFormField, {
                                            label: "Your editable wording",
                                            children: /*#__PURE__*/ _jsx(SpecraTextarea, {
                                                "aria-label": "Your editable wording",
                                                "data-testid": ids.wording,
                                                value: wording,
                                                onChange: (e)=>setWording(e.target.value)
                                            })
                                        }),
                                        /*#__PURE__*/ _jsx(SpecraButton, {
                                            disabled: save.isPending || !wording.trim(),
                                            onClick: ()=>save.mutate(),
                                            children: "Save wording"
                                        }),
                                        /*#__PURE__*/ _jsx(SpecraButton, {
                                            variant: "ghost",
                                            onClick: ()=>setEditing(''),
                                            children: "Cancel wording edit"
                                        })
                                    ]
                                })
                            ]
                        }, row.reuseId)),
                    opening && /*#__PURE__*/ _jsx(SpecraText, {
                        as: "p",
                        "aria-live": "polite",
                        children: "Opening the customer insight…"
                    }),
                    openError && /*#__PURE__*/ _jsx(SpecraText, {
                        as: "p",
                        role: "alert",
                        children: "The derivative is unavailable. Refresh evidence to check your access."
                    }),
                    derivative && /*#__PURE__*/ _jsxs("div", {
                        className: "space-y-2 rounded-md border p-3",
                        "data-testid": ids.summary,
                        children: [
                            /*#__PURE__*/ _jsxs(SpecraText, {
                                as: "p",
                                children: [
                                    derivative.title,
                                    " · v",
                                    derivative.version
                                ]
                            }),
                            /*#__PURE__*/ _jsx(SpecraBadge, {
                                variant: derivative.status === 'current' ? 'success' : 'warning',
                                children: themeReviewLabel(derivative.status)
                            }),
                            /*#__PURE__*/ _jsx(SpecraText, {
                                as: "p",
                                className: "whitespace-pre-wrap",
                                children: derivative.summary
                            }),
                            /*#__PURE__*/ _jsxs(SpecraText, {
                                as: "p",
                                size: "meta",
                                children: [
                                    derivative.provenance,
                                    " · reviewed",
                                    ' ',
                                    derivative.reviewedAt?.toDate().toLocaleDateString() || 'Not recorded'
                                ]
                            }),
                            /*#__PURE__*/ _jsxs(SpecraText, {
                                as: "p",
                                size: "meta",
                                children: [
                                    "Shared ",
                                    derivative.releasedAt?.toDate().toLocaleString() || 'date not recorded'
                                ]
                            }),
                            /*#__PURE__*/ _jsx(HistoryLink, {
                                itemId: derivative.libraryItemId
                            }),
                            /*#__PURE__*/ _jsxs(SpecraButton, {
                                variant: "secondary",
                                onClick: ()=>{
                                    generation.current++;
                                    setDerivative(undefined);
                                },
                                children: [
                                    "Back to ",
                                    THEME_RECEIVERS[receiver]
                                ]
                            })
                        ]
                    })
                ]
            })
        ]
    });
}
